import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonTitle,
    IonToolbar,
    IonSegment,
    IonSegmentButton,
    IonLabel,
    IonSelect,
    IonSelectOption,
    IonItem,
    useIonRouter
} from '@ionic/react';
import {
    analyticsOutline,
    trendingUpOutline,
    downloadOutline,
    calendarOutline,
    peopleOutline,
    barChartOutline,
    pieChartOutline,
    statsChartOutline,
    timeOutline,
    eyeOutline,
    thumbsUpOutline,
    chatbubbleOutline
} from 'ionicons/icons';
import { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api';
import Loader from '../components/Loader';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './View.css';

const AdminAnalytics = () => {
    const router = useIonRouter();

    const [selectedPeriod, setSelectedPeriod] = useState('7days');
    const [selectedChart, setSelectedChart] = useState('overview');
    const [analyticsData, setAnalyticsData] = useState({
        overview: {
            totalUsers: 0,
            totalUsersAbsolute: 0,
            activeUsers: 0,
            totalPolls: 0,
            totalPollsAbsolute: 0,
            totalVotes: 0,
            avgEngagement: 0,
            growthRate: 0,
            activePolls: 0
        },
        trends: {
            userGrowth: [],
            pollCreation: [],
            voteActivity: []
        },
        topPolls: [],
        demographics: {
            ageGroups: [],
            regions: []
        }
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAnalyticsData();
    }, [selectedPeriod]);

    const fetchAnalyticsData = async () => {
        try {
            setLoading(true);
            const userId = sessionStorage.getItem('user_id');
            const organizationId = sessionStorage.getItem('organization_id');

            const response = await axios.get(API_CONFIG.ANALYTICS, {
                params: {
                    user_id: userId,
                    organization_id: organizationId,
                    period: selectedPeriod
                }
            });

            if (response.data.success) {
                const data = response.data;

                // Backend now handles filtering by user_id for role 2
                const processedTopPolls = data.top_polls || [];

                // Calculate engagement rate based on total absolute users for better perspective
                const totalUsersAbs = parseInt(data.overview.total_users_absolute) || 0;
                const activeVoters = parseInt(data.overview.active_voters) || 0;
                
                const engagementRate = totalUsersAbs > 0
                    ? ((activeVoters / totalUsersAbs) * 100).toFixed(1)
                    : 0;

                // Calculate growth rate from trends
                const growthRate = calculateGrowthRate(data.user_growth);

                setAnalyticsData({
                    overview: {
                        totalUsers: parseInt(data.overview.total_users) || 0,
                        totalUsersAbsolute: totalUsersAbs,
                        activeUsers: activeVoters,
                        totalPolls: parseInt(data.overview.total_polls) || 0,
                        totalPollsAbsolute: parseInt(data.overview.total_polls_absolute) || 0,
                        totalVotes: parseInt(data.overview.total_votes) || 0,
                        avgEngagement: parseFloat(engagementRate),
                        growthRate: parseFloat(growthRate),
                        activePolls: parseInt(data.poll_stats?.active) || 0
                    },
                    trends: {
                        userGrowth: data.user_growth?.map(item => item.count) || [],
                        pollCreation: data.poll_creation?.map(item => item.count) || [],
                        voteActivity: data.vote_activity?.map(item => item.count) || []
                    },
                    topPolls: processedTopPolls.map((poll, index) => ({
                        id: poll.id,
                        question: poll.question,
                        votes: parseInt(poll.vote_count) || 0,
                        engagement: 85 - (index * 5), // Mock engagement for now
                        growth: 15 - (index * 3) // Mock growth for now
                    })),
                    demographics: {
                        ageGroups: data.age_demographics?.map(item => ({ 
                            range: item.age_group, 
                            count: parseInt(item.count),
                            percentage: totalUsersAbs > 0 ? ((parseInt(item.count) / totalUsersAbs) * 100).toFixed(1) : 0
                        })) || [],
                        regions: data.regional_distribution?.map(item => ({ 
                            name: item.region, 
                            count: parseInt(item.count),
                            percentage: totalUsersAbs > 0 ? ((parseInt(item.count) / totalUsersAbs) * 100).toFixed(1) : 0
                        })) || []
                    }
                });
            }
        } catch (error) {
            console.error('Error fetching analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    const calculateGrowthRate = (growthData) => {
        if (!growthData || growthData.length < 2) return 0;

        const recent = growthData.slice(-7); // Last 7 days
        const previous = growthData.slice(-14, -7); // Previous 7 days

        const recentTotal = recent.reduce((sum, item) => sum + parseInt(item.count || 0), 0);
        const previousTotal = previous.reduce((sum, item) => sum + parseInt(item.count || 0), 0);

        if (previousTotal === 0) return 0;

        return (((recentTotal - previousTotal) / previousTotal) * 100).toFixed(1);
    };

    const exportReport = (format) => {
        if (format === 'csv') {
            exportCSV();
        } else if (format === 'pdf') {
            exportPDF();
        }
    };

    const exportCSV = () => {
        const { overview, topPolls, demographics } = analyticsData;
        
        let csvContent = "data:text/csv;charset=utf-8,";
        csvContent += "Metric,Value\n";
        csvContent += `Total System Users,${overview.totalUsersAbsolute}\n`;
        csvContent += `New Users (Period),${overview.totalUsers}\n`;
        csvContent += `Active Voters,${overview.activeUsers}\n`;
        csvContent += `Total System Polls,${overview.totalPollsAbsolute}\n`;
        csvContent += `Active Polls,${overview.activePolls}\n`;
        csvContent += `Total Votes (Period),${overview.totalVotes}\n`;
        csvContent += `Avg Engagement,${overview.avgEngagement}%\n`;
        csvContent += `Growth Rate,${overview.growthRate}%\n\n`;

        csvContent += "Top Polls,Votes,Engagement\n";
        topPolls.forEach(poll => {
            csvContent += `"${poll.question}",${poll.votes},${poll.engagement}%\n`;
        });
        
        csvContent += "\nAge Demographics,Count,Percentage\n";
        demographics.ageGroups.forEach(group => {
            csvContent += `${group.range},${group.count},${group.percentage}%\n`;
        });

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `analytics_report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportPDF = () => {
        const doc = new jsPDF();
        const date = new Date().toLocaleDateString();
        
        // Header
        doc.setFontSize(22);
        doc.setTextColor(30, 41, 59);
        doc.text("PeopPoll Analytics Report", 20, 25);
        
        doc.setFontSize(10);
        doc.setTextColor(100, 116, 139);
        doc.text(`Generated on: ${date} | Period: ${selectedPeriod}`, 20, 32);
        
        // Overview Section
        doc.setFontSize(16);
        doc.setTextColor(30, 41, 59);
        doc.text("System Overview", 20, 50);
        
        const overviewData = [
            ["Metric", "Value"],
            ["Total System Users", analyticsData.overview.totalUsersAbsolute.toString()],
            ["New Users (Period)", analyticsData.overview.totalUsers.toString()],
            ["Active Voters", analyticsData.overview.activeUsers.toString()],
            ["Total System Polls", analyticsData.overview.totalPollsAbsolute.toString()],
            ["Active Polls", analyticsData.overview.activePolls.toString()],
            ["Total Votes (Period)", analyticsData.overview.totalVotes.toString()],
            ["Avg Engagement", `${analyticsData.overview.avgEngagement}%`],
            ["Growth Rate", `${analyticsData.overview.growthRate}%`]
        ];
        
        doc.autoTable({
            startY: 55,
            head: [overviewData[0]],
            body: overviewData.slice(1),
            theme: 'striped',
            headStyles: { fillColor: [79, 70, 229] }
        });
        
        // Top Polls Section
        doc.setFontSize(16);
        doc.text("Top Performing Polls", 20, doc.lastAutoTable.finalY + 15);
        
        const pollData = analyticsData.topPolls.map(p => [p.question, p.votes.toString(), `${p.engagement}%`]);
        
        doc.autoTable({
            startY: doc.lastAutoTable.finalY + 20,
            head: [['Question', 'Votes', 'Engagement']],
            body: pollData,
            theme: 'striped',
            headStyles: { fillColor: [5, 150, 105] }
        });
        
        // Footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
        }
        
        doc.save(`PeopPoll_Analytics_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const renderOverviewCards = () => {
        const cards = [
            {
                title: 'TOTAL USERS',
                value: analyticsData.overview.totalUsersAbsolute.toLocaleString(),
                icon: peopleOutline,
                color: 'text-blue-500',
                bgColor: 'bg-blue-50',
                change: `+${analyticsData.overview.totalUsers} new`,
                changeType: 'positive'
            },
            {
                title: 'ACTIVE USERS',
                value: analyticsData.overview.activeUsers.toLocaleString(),
                icon: eyeOutline,
                color: 'text-green-500',
                bgColor: 'bg-green-50',
                change: 'Current',
                changeType: 'positive'
            },
            {
                title: 'ACTIVE POLLS',
                value: analyticsData.overview.activePolls.toLocaleString(),
                icon: barChartOutline,
                color: 'text-indigo-500',
                bgColor: 'bg-indigo-50',
                change: `${analyticsData.overview.totalPollsAbsolute} total`,
                changeType: 'positive'
            },
            {
                title: 'TOTAL VOTES',
                value: analyticsData.overview.totalVotes.toLocaleString(),
                icon: thumbsUpOutline,
                color: 'text-orange-500',
                bgColor: 'bg-orange-50',
                change: 'Period',
                changeType: 'positive'
            },
            {
                title: 'AVG ENGAGEMENT',
                value: `${analyticsData.overview.avgEngagement}%`,
                icon: trendingUpOutline,
                color: 'text-pink-500',
                bgColor: 'bg-pink-50',
                change: 'System-wide',
                changeType: 'positive'
            },
            {
                title: 'GROWTH RATE',
                value: `${analyticsData.overview.growthRate}%`,
                icon: statsChartOutline,
                color: 'text-cyan-500',
                bgColor: 'bg-cyan-50',
                change: '+3.2%',
                changeType: 'positive'
            }
        ];

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {cards.map((card, index) => (
                    <div key={index} className="analytics-card-glass stat-card-premium">
                        <div className="flex items-center justify-between mb-6">
                            <div className={`${card.bgColor} ${card.color} rounded-2xl p-4 shadow-sm`}>
                                <IonIcon icon={card.icon} className="text-3xl" />
                            </div>
                            <div className={`text-xs font-bold px-3 py-1 rounded-full ${card.changeType === 'positive' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                {card.change}
                            </div>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-slate-400 tracking-widest mb-1">{card.title}</p>
                            <h3 className="text-4xl font-black text-slate-800 tracking-tight">{card.value}</h3>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderTrendsChart = () => {
        // Prepare data for the last 7 days
        const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const userGrowthData = analyticsData.trends.userGrowth.slice(-7);
        const pollCreationData = analyticsData.trends.pollCreation.slice(-7);
        const voteActivityData = analyticsData.trends.voteActivity.slice(-7);

        // Ensure we have 7 data points - fill with zeros if needed
        while (userGrowthData.length < 7) userGrowthData.unshift(0);
        while (pollCreationData.length < 7) pollCreationData.unshift(0);
        while (voteActivityData.length < 7) voteActivityData.unshift(0);

        // Get the maximum value for scaling
        const allValues = [...userGrowthData, ...pollCreationData, ...voteActivityData];
        const maxValue = Math.max(...allValues, 1); // At least 1 to avoid division by zero

        return (
            <div className="trend-chart-container mb-12">
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '32px'
                }}>
                    <h3 style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#1E293B',
                        margin: 0
                    }}>
                        Activity Trends
                    </h3>
                    <div style={{ display: 'flex', gap: '16px' }}>
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '13px',
                            color: '#64748B',
                            fontWeight: '600'
                        }}>
                            <div style={{
                                width: '12px',
                                height: '12px',
                                background: '#4F46E5',
                                borderRadius: '50%',
                                marginRight: '8px'
                            }}></div>
                            User Growth
                        </span>
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '13px',
                            color: '#64748B',
                            fontWeight: '600'
                        }}>
                            <div style={{
                                width: '12px',
                                height: '12px',
                                background: '#059669',
                                borderRadius: '50%',
                                marginRight: '8px'
                            }}></div>
                            Poll Creation
                        </span>
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '13px',
                            color: '#64748B',
                            fontWeight: '600'
                        }}>
                            <div style={{
                                width: '12px',
                                height: '12px',
                                background: '#7C3AED',
                                borderRadius: '50%',
                                marginRight: '8px'
                            }}></div>
                            Vote Activity
                        </span>
                    </div>
                </div>

                <div style={{
                    height: '280px',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: '16px',
                    padding: '0 8px'
                }}>
                    {days.map((day, index) => {
                        const userHeight = maxValue > 0 ? (userGrowthData[index] / maxValue) * 100 : 0;
                        const pollHeight = maxValue > 0 ? (pollCreationData[index] / maxValue) * 100 : 0;
                        const voteHeight = maxValue > 0 ? (voteActivityData[index] / maxValue) * 100 : 0;

                        return (
                            <div key={index} style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <div style={{
                                    width: '100%',
                                    position: 'relative',
                                    height: '220px',
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    gap: '2px'
                                }}>
                                    {/* User Growth Bar */}
                                    <div style={{
                                        flex: 1,
                                        background: 'linear-gradient(to top, #4F46E5, #6366F1)',
                                        borderRadius: '6px 6px 0 0',
                                        height: `${Math.max(userHeight, 5)}%`,
                                        minHeight: userGrowthData[index] > 0 ? '8px' : '0',
                                        transition: 'all 0.5s ease',
                                        position: 'relative',
                                        boxShadow: '0 -2px 4px rgba(79, 70, 229, 0.2)'
                                    }}>
                                        {userGrowthData[index] > 0 && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '-24px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                color: '#4F46E5',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {userGrowthData[index]}
                                            </div>
                                        )}
                                    </div>

                                    {/* Poll Creation Bar */}
                                    <div style={{
                                        flex: 1,
                                        background: 'linear-gradient(to top, #059669, #10B981)',
                                        borderRadius: '6px 6px 0 0',
                                        height: `${Math.max(pollHeight, 5)}%`,
                                        minHeight: pollCreationData[index] > 0 ? '8px' : '0',
                                        transition: 'all 0.5s ease',
                                        position: 'relative',
                                        boxShadow: '0 -2px 4px rgba(5, 150, 105, 0.2)'
                                    }}>
                                        {pollCreationData[index] > 0 && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '-24px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                color: '#059669',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {pollCreationData[index]}
                                            </div>
                                        )}
                                    </div>

                                    {/* Vote Activity Bar */}
                                    <div style={{
                                        flex: 1,
                                        background: 'linear-gradient(to top, #7C3AED, #9333EA)',
                                        borderRadius: '6px 6px 0 0',
                                        height: `${Math.max(voteHeight, 5)}%`,
                                        minHeight: voteActivityData[index] > 0 ? '8px' : '0',
                                        transition: 'all 0.5s ease',
                                        position: 'relative',
                                        boxShadow: '0 -2px 4px rgba(124, 58, 237, 0.2)'
                                    }}>
                                        {voteActivityData[index] > 0 && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '-24px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                color: '#7C3AED',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {voteActivityData[index]}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    color: '#94A3B8',
                                    fontWeight: '600',
                                    marginTop: '8px'
                                }}>
                                    {day}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Show message if no data */}
                {maxValue === 1 && (
                    <div style={{
                        textAlign: 'center',
                        padding: '32px',
                        color: '#64748B',
                        fontSize: '14px'
                    }}>
                        No activity data available for the selected period. Start creating polls and collecting votes to see trends!
                    </div>
                )}
            </div>
        );
    };

    const renderTopPolls = () => {
        return (
            <div className="analytics-card-glass p-8 mb-12 border-none">
                <h3 className="text-2xl font-black text-slate-800 mb-8 flex items-center gap-3">
                    <IonIcon icon={trendingUpOutline} color="success" />
                    Top Performing Intelligence
                </h3>
                <div className="space-y-4">
                    {analyticsData.topPolls.map((poll, index) => (
                        <div key={poll.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex items-center space-x-4">
                                <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                                    {index + 1}
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-800">{poll.question}</h4>
                                    <p className="text-sm text-gray-600">{poll.votes.toLocaleString()} votes</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-lg font-bold text-blue-600">{poll.engagement}%</div>
                                <div className={`text-sm ${poll.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {poll.growth >= 0 ? '+' : ''}{poll.growth}%
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderDemographics = () => {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 text-slate-700">
                {/* Age Demographics */}
                <div className="analytics-card-glass p-8 border-none">
                    <h3 className="text-xl font-bold text-slate-800 mb-8 flex items-center gap-3">
                        <IonIcon icon={peopleOutline} color="primary" />
                        Age Distribution
                    </h3>
                    <div className="space-y-4">
                        {analyticsData.demographics.ageGroups.map((group, index) => (
                            <div key={index} className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="font-medium text-gray-700">{group.range}</span>
                                    <span className="text-gray-600">{group.count} users ({group.percentage}%)</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                                        style={{ width: `${group.percentage}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                        {analyticsData.demographics.ageGroups.length === 0 && (
                            <p className="text-sm text-slate-400 text-center py-4">No age data available</p>
                        )}
                    </div>
                </div>

                {/* Regional Demographics */}
                <div className="analytics-card-glass p-8 border-none">
                    <h3 className="text-xl font-bold text-slate-800 mb-8 flex items-center gap-3">
                        <IonIcon icon={statsChartOutline} color="success" />
                        Regional Density
                    </h3>
                    <div className="space-y-4">
                        {analyticsData.demographics.regions.map((region, index) => (
                            <div key={index} className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="font-medium text-gray-700">{region.name}</span>
                                    <span className="text-gray-600">{region.count} users ({region.percentage}%)</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-green-500 h-2 rounded-full transition-all duration-500"
                                        style={{ width: `${region.percentage}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                        {analyticsData.demographics.regions.length === 0 && (
                            <p className="text-sm text-slate-400 text-center py-4">No regional data available</p>
                        )}
                    </div>
                </div>
            </div>
        );
    };


    return (
        <IonPage className="view-page">
            <IonHeader className="ion-no-border" translucent={true}>
                <IonToolbar className="header-toolbar frosted-header">
                    <IonButtons slot="start">
                        <IonBackButton text="Back" className="custom-back-button" defaultHref="/org-admin" style={{ '--color': '#1e293b' }} />
                    </IonButtons>
                    <IonTitle style={{ fontFamily: 'Montserrat', fontWeight: '700', color: '#1e293b' }}>Analytics Intelligence</IonTitle>
                    <IonButtons slot="end">
                        <IonButton className="export-btn-premium" onClick={() => exportReport('pdf')}>
                            <IonIcon icon={downloadOutline} slot="start" />
                            EXTERNAL EXPORT
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="view-content">
                {loading && <Loader />}
                <div className="polls-container">

                    {/* Controls Section */}
                    <div className="glass-card-beautified mb-8 p-6">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                            <IonSegment
                                value={selectedChart}
                                onIonChange={(e) => setSelectedChart(e.detail.value)}
                                className="custom-segment-premium"
                                style={{ maxWidth: '600px', background: '#f1f5f9', borderRadius: '16px', padding: '4px' }}
                            >
                                <IonSegmentButton value="overview">
                                    <IonLabel style={{ fontWeight: '600' }}>OVERVIEW</IonLabel>
                                </IonSegmentButton>
                                <IonSegmentButton value="trends">
                                    <IonLabel style={{ fontWeight: '600' }}>TRENDS</IonLabel>
                                </IonSegmentButton>
                                <IonSegmentButton value="demographics">
                                    <IonLabel style={{ fontWeight: '600' }}>DEMOGRAPHICS</IonLabel>
                                </IonSegmentButton>
                            </IonSegment>

                            <div className="flex items-center gap-4 w-full md:w-auto">
                                <div className="flex items-center bg-gray-100 rounded-2xl px-4 py-1 flex-1 md:flex-initial">
                                    <IonIcon icon={calendarOutline} color="success" />
                                    <IonSelect
                                        value={selectedPeriod}
                                        onIonChange={(e) => setSelectedPeriod(e.detail.value)}
                                        interface="popover"
                                        className="text-sm font-semibold"
                                    >
                                        <IonSelectOption value="7days">Last 7 Days</IonSelectOption>
                                        <IonSelectOption value="30days">Last 30 Days</IonSelectOption>
                                        <IonSelectOption value="90days">Last 90 Days</IonSelectOption>
                                        <IonSelectOption value="1year">Last Year</IonSelectOption>
                                    </IonSelect>
                                </div>

                                <div className="flex gap-2">
                                    <IonButton fill="clear" color="dark" onClick={() => exportReport('csv')} className="text-xs font-bold">
                                        <IonIcon icon={downloadOutline} slot="start" />
                                        CSV
                                    </IonButton>
                                    <IonButton fill="clear" color="dark" onClick={() => exportReport('pdf')} className="text-xs font-bold">
                                        <IonIcon icon={downloadOutline} slot="start" />
                                        PDF
                                    </IonButton>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Chart Content */}
                    {selectedChart === 'overview' && renderOverviewCards()}
                    {selectedChart === 'trends' && renderTrendsChart()}
                    {selectedChart === 'demographics' && renderDemographics()}

                    {/* Always show top polls if there's data */}
                    {analyticsData.topPolls.length > 0 && renderTopPolls()}

                    {/* Real-time Insights */}
                    <div className="insight-banner-premium mb-12">
                        <div className="flex items-center gap-6 mb-8">
                            <div className="insight-icon-container">
                                <IonIcon icon={analyticsOutline} style={{ fontSize: '28px', color: '#FFFFFF' }} />
                            </div>
                            <div>
                                <h3 className="text-2xl font-black text-slate-800 leading-none mb-2">
                                    LIVE CORE INTELLIGENCE
                                </h3>
                                <p className="text-slate-500 font-medium tracking-wide text-xs">
                                    REAL-TIME PERFORMANCE ARCHITECTURE
                                </p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                            <div className="bg-white bg-opacity-60 backdrop-blur-md rounded-2xl p-6 text-center shadow-sm">
                                <div className="text-4xl font-black text-green-600 mb-1">
                                    {analyticsData.overview.activeUsers}
                                </div>
                                <div className="text-[10px] font-bold text-slate-400 tracking-widest uppercase">
                                    CURRENT ACTIVE NODES
                                </div>
                            </div>
                            <div className="bg-white bg-opacity-60 backdrop-blur-md rounded-2xl p-6 text-center shadow-sm">
                                <div className="text-4xl font-black text-blue-600 mb-1">
                                    {analyticsData.overview.totalPolls}
                                </div>
                                <div className="text-[10px] font-bold text-slate-400 tracking-widest uppercase">
                                    POLLS GENERATED PERIOD
                                </div>
                            </div>
                            <div className="bg-white bg-opacity-60 backdrop-blur-md rounded-2xl p-6 text-center shadow-sm">
                                <div className="text-4xl font-black text-purple-600 mb-1">
                                    {analyticsData.overview.totalVotes}
                                </div>
                                <div className="text-[10px] font-bold text-slate-400 tracking-widest uppercase">
                                    SYSTEM VOTES PERIOD
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </IonContent>
        </IonPage>
    );
};

export default AdminAnalytics;
